/*
  The redesigned cart (snippets/cart-panel.liquid), on top of cart-drawer.js,
  which still does every change to the cart and every redraw.

  Three things are added here:

    The count in the heading and the Buy more, save more block change on
    the tap, before Shopify answers, from the quantities on screen, the
    same way cart-drawer.js updates the bag in the header. The redraw that
    follows brings Shopify's own numbers.

    Before a removal that lowers the discount, the row asks first, as in
    Rosa-Mae-Buy-more-save-more.pdf: "Removing this drops your discount
    from 15% to 10%. The rest of your cart goes up $5.00." with Keep it and
    Remove. For the − button it says "Removing one" and "Remove 1".

    "+ Add" on the add-on rows puts that product in the cart with one
    request that also asks for the cart's new markup, and hands the answer
    to the drawer or the page to draw.
*/
(() => {
  if (window.cartPanelReady) return;
  window.cartPanelReady = true;

  const pct = (n) => (n >= 4 ? 20 : n === 3 ? 15 : n === 2 ? 10 : 0);
  const unit = (cents, n) => Math.round((cents * (100 - pct(n))) / 100);

  /* A row reading Free is the free gift, and is not counted. */
  const isFree = (row) => Boolean(row.querySelector('.cart-item__price-final')) && Number(row.dataset.unitPrice) === 0;

  const quantityOf = (row) => {
    const value = row.querySelector('.cart-qty__value');
    return value ? parseInt(value.textContent, 10) || 0 : 0;
  };

  /* The rows on screen that count; rows hidden by a removal on its way are
     left out. */
  const countedRows = (host) =>
    Array.from(host.querySelectorAll('.cart-item')).filter((row) => row.style.display !== 'none' && !isFree(row));

  const countOf = (host) => countedRows(host).reduce((sum, row) => sum + quantityOf(row), 0);

  function sync(host) {
    if (!host.querySelector('[data-cx-mm]')) return;
    const n = countOf(host);

    const count = host.querySelector('[data-cx-count]');
    if (count) count.textContent = `(${n})`;

    if (window.MixMatch && window.MixMatch.renderBox) {
      host.querySelectorAll('[data-cx-mm] [data-mm-box]').forEach((box) => window.MixMatch.renderBox(box, n));
    }
  }

  /* Asking before a removal --------------------------------------------- */

  /*
    What a removal does to the rest of the cart: every unit that stays, at
    the tier the cart drops to, less the same units at the tier it has now.
    Each price from the original, rounded to the cent like the checkout.
  */
  function riseOf(host, row, removed, after, before) {
    return countedRows(host).reduce((sum, other) => {
      const original = Number(other.dataset.unitOriginal);
      if (!Number.isFinite(original)) return sum;
      const stays = quantityOf(other) - (other === row ? removed : 0);
      return sum + stays * (unit(original, after) - unit(original, before));
    }, 0);
  }

  function closeQuestion(host) {
    host.querySelectorAll('[data-cx-ask]').forEach((ask) => ask.remove());
  }

  function ask(host, button, text, confirmLabel) {
    closeQuestion(host);

    const row = button.closest('.cart-item');
    const detail = row.querySelector('.cx-row__detail') || row;

    const box = document.createElement('div');
    box.className = 'cx-ask';
    box.setAttribute('data-cx-ask', '');
    box.setAttribute('role', 'group');
    box.innerHTML = `
      <p class="cx-ask__text" role="status"></p>
      <div class="cx-ask__actions">
        <button type="button" class="cx-ask__button cx-ask__button--dark" data-cx-keep>Keep it</button>
        <button type="button" class="cx-ask__button" data-cx-remove></button>
      </div>`;
    box.querySelector('.cx-ask__text').textContent = text;
    box.querySelector('[data-cx-remove]').textContent = confirmLabel;

    box.querySelector('[data-cx-keep]').addEventListener('click', () => box.remove());
    box.querySelector('[data-cx-remove]').addEventListener('click', () => {
      box.remove();
      button.dataset.cxConfirmed = 'true';
      button.click();
    });

    detail.appendChild(box);
    box.querySelector('[data-cx-keep]').focus({ preventScroll: true });
  }

  /*
    Runs before cart-drawer.js sees the tap, in the capture phase: when the
    removal would lower the discount, the tap is held and the row asks.
  */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('[data-cart-qty]');
      const host = button && button.closest('cart-drawer, cart-page');
      if (!host || button.disabled) return;

      if (button.dataset.cxConfirmed) {
        delete button.dataset.cxConfirmed;
        return;
      }

      /* The bin removes the row; the − one item, also when it is the last. */
      const row = button.closest('.cart-item');
      const removeAll = button.matches('.cart-item__remove');
      const isMinus = !removeAll && !button.matches('.cart-qty__button:last-of-type');
      if (!row || isFree(row) || (!removeAll && !isMinus) || !window.MixMatch) return;

      const removed = removeAll ? quantityOf(row) : 1;
      const before = countOf(host);
      const after = before - removed;
      if (pct(after) >= pct(before)) return;

      const rise = riseOf(host, row, removed, after, before);
      if (rise <= 0) return;

      event.preventDefault();
      event.stopPropagation();

      const subject = removeAll ? 'Removing this' : 'Removing one';
      const change = pct(after) > 0
        ? `drops your discount from ${pct(before)}% to ${pct(after)}%`
        : `ends your ${pct(before)}% discount`;
      const text = `${subject} ${change}. The rest of your cart goes up ${window.MixMatch.money(rise)}.`;

      ask(host, button, text, removeAll ? 'Remove' : 'Remove 1');
    },
    true
  );

  // After cart-drawer.js has handled the tap on the element itself.
  document.addEventListener('click', (event) => {
    const host = event.target.closest('cart-drawer, cart-page');
    if (!host) return;

    if (event.target.closest('[data-cart-qty]')) {
      sync(host);
      return;
    }

    const add = event.target.closest('[data-cx-add]');
    if (add) addItem(host, add);
  });

  async function addItem(host, button) {
    if (button.getAttribute('aria-busy')) return;
    button.setAttribute('aria-busy', 'true');

    const sections = typeof host.getSectionsToRender === 'function'
      ? host.getSectionsToRender().map((section) => section.id)
      : [];
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.cxAdd), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();

      if (!response.ok || state.status) {
        button.removeAttribute('aria-busy');
        if (typeof host.showNotice === 'function') host.showNotice(state.description || '');
        return;
      }

      if (typeof host.renderContents === 'function') host.renderContents(state);
    } catch (error) {
      button.removeAttribute('aria-busy');
      if (typeof host.showNotice === 'function') host.showNotice('');
    }
  }
})();
